import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { BookOpen, Mail, Lock, User, Eye, EyeOff, Zap } from "lucide-react";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default function Login() {
  const navigate = useNavigate();
  const { login, register } = useAuth();
  const [mode, setMode] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      if (mode === "login") {
        await login(email, password);
      } else {
        if (!name.trim()) {
          setError("Please enter your name");
          setLoading(false);
          return;
        }
        await register(name, email, password);
      }
      toast.success(mode === "login" ? "Welcome back!" : "Account created!");
      navigate("/");
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen bg-neo-bg dark:bg-[#141414] flex items-center justify-center px-4" data-testid="login-page">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 rounded-xl bg-neo-yellow border-[3px] border-neo-border dark:border-[#444] shadow-[4px_4px_0px_0px_#1A1A1A] dark:shadow-[4px_4px_0px_0px_#444] flex items-center justify-center mx-auto mb-4">
            <BookOpen className="w-8 h-8 text-neo-text" />
          </div>
          <h1 className="font-heading font-extrabold text-4xl sm:text-5xl tracking-tight dark:text-white">PhD Reader</h1>
          <p className="text-neo-text-secondary dark:text-gray-400 mt-2">Gamify your academic reading</p>
        </div>

        {/* Auth Card */}
        <div className="neo-card dark:bg-[#1E1E1E] dark:border-[#444] dark:shadow-[4px_4px_0px_0px_#444]">
          {/* Mode Toggle */}
          <div className="flex mb-6 border-2 border-neo-border dark:border-[#444] rounded-lg overflow-hidden">
            <button
              onClick={() => { setMode("login"); setError(""); }}
              className={`flex-1 py-3 font-heading font-bold text-sm transition-all ${
                mode === "login" ? "bg-neo-yellow text-neo-text" : "bg-white dark:bg-[#2A2A2A] dark:text-white"
              }`}
              data-testid="login-tab"
            >
              Sign In
            </button>
            <button
              onClick={() => { setMode("register"); setError(""); }}
              className={`flex-1 py-3 font-heading font-bold text-sm transition-all border-l-2 border-neo-border dark:border-[#444] ${
                mode === "register" ? "bg-neo-yellow text-neo-text" : "bg-white dark:bg-[#2A2A2A] dark:text-white"
              }`}
              data-testid="register-tab"
            >
              Create Account
            </button>
          </div>

          {error && (
            <div className="mb-4 p-3 rounded-lg border-2 border-neo-red bg-neo-red/10 text-sm dark:text-white" data-testid="auth-error">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            {mode === "register" && (
              <div>
                <label className="text-xs uppercase tracking-[0.15em] font-semibold text-neo-text-secondary dark:text-gray-400 block mb-1">Name</label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neo-text-secondary dark:text-gray-400" />
                  <Input
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Your name"
                    className="pl-10 border-2 border-neo-border dark:border-[#444] dark:bg-[#2A2A2A] dark:text-white rounded-lg py-3 focus:ring-0 focus:border-[3px] focus:border-neo-yellow"
                    data-testid="register-name-input"
                  />
                </div>
              </div>
            )}
            <div>
              <label className="text-xs uppercase tracking-[0.15em] font-semibold text-neo-text-secondary dark:text-gray-400 block mb-1">Email</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neo-text-secondary dark:text-gray-400" />
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  required
                  className="pl-10 border-2 border-neo-border dark:border-[#444] dark:bg-[#2A2A2A] dark:text-white rounded-lg py-3 focus:ring-0 focus:border-[3px] focus:border-neo-yellow"
                  data-testid="email-input"
                />
              </div>
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.15em] font-semibold text-neo-text-secondary dark:text-gray-400 block mb-1">Password</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neo-text-secondary dark:text-gray-400" />
                <Input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter password"
                  required
                  minLength={6}
                  className="pl-10 pr-10 border-2 border-neo-border dark:border-[#444] dark:bg-[#2A2A2A] dark:text-white rounded-lg py-3 focus:ring-0 focus:border-[3px] focus:border-neo-yellow"
                  data-testid="password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-neo-text-secondary dark:text-gray-400"
                  data-testid="toggle-password-visibility"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <button
              type="submit"
              disabled={loading}
              className={`neo-btn w-full py-3 text-base ${loading ? "bg-gray-200 text-gray-500 border-gray-300 shadow-none cursor-not-allowed" : "neo-btn-primary"}`}
              data-testid="auth-submit-btn"
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <div className="w-5 h-5 border-2 border-neo-text border-t-transparent rounded-full animate-spin" />
                  {mode === "login" ? "Signing in..." : "Creating account..."}
                </span>
              ) : (
                <span className="flex items-center justify-center gap-2">
                  <Zap className="w-4 h-4" />
                  {mode === "login" ? "Sign In" : "Create Account"}
                </span>
              )}
            </button>
          </form>

          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t-2 border-neo-border/20 dark:border-[#444]/40" />
            </div>
            <div className="relative flex justify-center text-xs uppercase tracking-[0.15em]">
              <span className="bg-white dark:bg-[#1E1E1E] px-3 text-neo-text-secondary dark:text-gray-400 font-semibold">or</span>
            </div>
          </div>

          {mode === "login" && (
            <div className="text-center mb-4">
              <a
                href="/forgot-password"
                className="text-sm text-neo-text-secondary dark:text-gray-400 hover:text-neo-text dark:hover:text-white underline underline-offset-2"
                data-testid="forgot-password-link"
              >
                Forgot your password?
              </a>
            </div>
          )}

          <button
            onClick={handleGoogleLogin}
            className="neo-btn bg-white dark:bg-[#2A2A2A] dark:text-white w-full py-3 flex items-center justify-center gap-3 text-base"
            data-testid="google-login-btn"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24">
              <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
              <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
              <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
              <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
            </svg>
            Continue with Google
          </button>
        </div>
      </div>
    </div>
  );
}
